import { ArrowUpRight } from 'lucide-react'
import { Fragment } from 'react'
import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

// mbscan manager-client の同名部品と共通。片側だけで変えない
// 使い方のルール: docs/develop/client_ui/components/screen-tabs.md

export type ScreenTabItem<T extends string = string> = {
  id: T
  label: string
  disabled?: boolean
  disabledReason?: string
  // 別画面へのリンクも同じ見た目で並べつつ、ルーターの選択は利用側へ委ねる。
  renderLink?: (props: { className: string; children: ReactNode }) => ReactNode
}

// 画面内タブ。underline が標準。pills はタブの中の小さな切替（ダッシュボードの表示形式など）に使う。
// （経緯: docs/issues/20260827_03_client-ui-feedback-round2.md）
export function ScreenTabs<T extends string>({
  items,
  value,
  onChange,
  variant,
  'aria-label': ariaLabel,
  className,
}: {
  items: ReadonlyArray<ScreenTabItem<T>>
  value: T
  onChange: (next: T) => void
  variant: 'pills' | 'underline'
  'aria-label': string
  className?: string
}) {
  if (variant === 'pills') {
    const inactive = 'text-muted-foreground hover:text-foreground'
    return (
      <div className={cn('rounded-2xl bg-muted p-1.5', className)}>
        <nav className="flex gap-1 overflow-x-auto" aria-label={ariaLabel}>
          {items.map((item) =>
            item.renderLink ? (
              <Fragment key={item.id}>
                {item.renderLink({
                  className: cn('shrink-0 rounded-xl px-4 py-2.5 text-sm font-bold', inactive),
                  children: (
                    <>
                      {item.label}
                      <ArrowUpRight className="ml-1 inline size-3.5 align-[-2px]" />
                    </>
                  ),
                })}
              </Fragment>
            ) : (
              <button
                key={item.id}
                type="button"
                disabled={item.disabled}
                title={item.disabledReason}
                onClick={() => {
                  onChange(item.id)
                }}
                aria-current={value === item.id ? 'page' : undefined}
                className={cn(
                  'shrink-0 rounded-xl px-4 py-2.5 text-sm font-bold',
                  value === item.id ? 'bg-card text-primary shadow-sm' : inactive,
                  item.disabled === true && 'cursor-not-allowed opacity-50',
                )}
              >
                {item.label}
              </button>
            ),
          )}
        </nav>
      </div>
    )
  }

  return (
    <nav className={cn('flex gap-1 overflow-x-auto border-b', className)} aria-label={ariaLabel}>
      {items.map((item) =>
        item.renderLink ? (
          <Fragment key={item.id}>
            {item.renderLink({
              className: 'shrink-0 border-b-2 border-transparent px-4 py-3 text-sm font-bold text-muted-foreground',
              children: (
                <>
                  {item.label}
                  <ArrowUpRight className="ml-1 inline size-3.5 align-[-2px]" />
                </>
              ),
            })}
          </Fragment>
        ) : (
          <button
            key={item.id}
            type="button"
            disabled={item.disabled}
            title={item.disabledReason}
            onClick={() => {
              onChange(item.id)
            }}
            aria-current={value === item.id ? 'page' : undefined}
            className={cn(
              value === item.id
                ? 'shrink-0 border-b-2 border-primary px-4 py-3 text-sm font-black text-primary'
                : 'shrink-0 border-b-2 border-transparent px-4 py-3 text-sm font-bold text-muted-foreground',
              item.disabled === true && 'cursor-not-allowed opacity-50',
            )}
          >
            {item.label}
          </button>
        ),
      )}
    </nav>
  )
}
